// [phase12cfd-v1] Signal Lanes (market-cap segmentation) client.
//
// Same-origin GETs over /api/v1/signal/lanes/*. Mirrors the signalClient
// shape but does not import from it (loose coupling — lanes are an
// additive view, not a Signal-core dependency).
//
// Hard contracts:
//   * Lanes are advisory; the client never has any write/POST entry points.
//   * Every response carries advisory_only / affects_official_hit_rate /
//     no_combined_pool flags — the UI must honor them.

import { apiV1Base } from "./apiConfig";

const BASE = apiV1Base();
const TIMEOUT_MS = 10_000;

export type LaneId =
  | "micro_cap_signal"
  | "small_cap_signal"
  | "large_cap_signal"
  | "unknown_market_cap";

export interface LaneCard {
  lane_id: LaneId;
  lane_label: string;
  description: string;
  today_candidate_count: number;
  window_candidate_count: number;
  same_day_hit_rate: number | null;
  full_day_hit_rate: number | null;
  five_day_hit_rate: number | null;
  ten_day_hit_rate: number | null;
  late_winner_rate: number | null;
  false_negative_rate: number | null;
  false_positive_rate: number | null;
  sample_size_warning: boolean;
}

export interface LaneSummary {
  status: string;
  date: string;
  candidate_count_total: number;
  lanes: LaneCard[];
  unknown_lane: LaneCard;
  performance_window_dates: string[];
  performance_window_days: number | null;
  advisory_only: true;
  affects_official_hit_rate: false;
  no_auto_strategy_change: true;
  no_combined_pool: true;
  scoreboard_presentation_rule: string;
}

export interface RoutedCandidate {
  symbol: string;
  price: number | null;
  float_value: number | null;
  rank: number | null;
  score: number | null;
  lane_type: LaneId;
  lane_label: string;
  market_cap_estimate_usd: number | null;
  lane_classification_source: "override" | "price_x_float" | "unknown";
  lane_classification_reason: string;
  lane_view_advisory_only: true;
  lane_view_affects_official_hit_rate: false;
}

export interface LaneCandidates {
  status: "OK" | "CANDIDATES_MISSING";
  date: string;
  candidate_count_total: number;
  rows_by_lane: Record<LaneId, RoutedCandidate[]>;
  counts_by_lane: Record<LaneId, number>;
  lane_labels: Record<LaneId, string>;
  lane_descriptions: Record<LaneId, string>;
  no_combined_pool: true;
  advisory_only: true;
  affects_official_hit_rate: false;
}

export interface LanePerformanceRow {
  candidate_count: number;
  same_day_hit_rate: number | null;
  five_day_hit_rate: number | null;
  ten_day_hit_rate: number | null;
  late_winner_rate: number | null;
  false_negative_rate: number | null;
  false_positive_rate: number | null;
  sample_size_warning: boolean;
  lane_label: string;
}

export interface LanePerformance {
  status: string;
  window_days: number | null;
  window_dates: string[];
  lane_performance: Record<LaneId, LanePerformanceRow>;
  today_counts_by_lane: Record<LaneId, number>;
  advisory_only: true;
  affects_official_hit_rate: false;
  no_combined_pool: true;
  limitations: string[];
}

export type Envelope<T> =
  | { ok: true; data: T; request_id?: string }
  | { ok: false; error: { message: string }; request_id?: string };

async function getJson<T>(path: string): Promise<Envelope<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
    });
    const body = await res.json();
    if (!res.ok || body?.ok === false) {
      return {
        ok: false,
        error: { message: body?.error?.message || `HTTP ${res.status}` },
      };
    }
    return { ok: true, data: body.data as T };
  } catch (err) {
    return {
      ok: false,
      error: { message: (err as Error)?.message || "fetch failed" },
    };
  } finally {
    clearTimeout(timer);
  }
}

export function getSignalLanesSummary(): Promise<Envelope<LaneSummary>> {
  return getJson<LaneSummary>("/signal/lanes/summary");
}

export function getSignalLanesCandidates(
  date?: string,
): Promise<Envelope<LaneCandidates>> {
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  return getJson<LaneCandidates>(`/signal/lanes/candidates${q}`);
}

export function getSignalLanesPerformance(): Promise<Envelope<LanePerformance>> {
  return getJson<LanePerformance>("/signal/lanes/performance");
}

// Re-exported the legacy Outcome Intelligence summary so the panel
// can render it without taking another dependency. Shape matches the
// existing /api/v1/signal/outcome-learning/summary contract.
export interface OutcomeLearningSummary {
  status: string;
  dates_tracked: number;
  candidates_tracked: number;
  same_day_hit_rate: number | null;
  five_day_hit_rate: number | null;
  ten_day_hit_rate: number | null;
  late_winner_rate: number | null;
  false_negative_rate: number | null;
  false_positive_rate: number | null;
  consistent_winner_rate: number | null;
  missed_major_winner_count: number;
  counts: Record<string, number>;
  same_day_counts: Record<string, number>;
  advisory_only: true;
  affects_official_hit_rate: false;
  no_auto_strategy_change: true;
}

export function getSignalOutcomeLearningSummary(): Promise<
  Envelope<OutcomeLearningSummary>
> {
  return getJson<OutcomeLearningSummary>("/signal/outcome-learning/summary");
}
